import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result, Typography, Space } from 'antd';
import { ReloadOutlined, HomeOutlined, BugOutlined } from '@ant-design/icons';
import i18n from '@/i18n';

const { Text, Paragraph } = Typography;

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Global ErrorBoundary component to catch React render errors
 * and prevent white screen crashes.
 *
 * This catches:
 * - Errors during rendering
 * - Errors in lifecycle methods
 * - Errors in constructors of child components
 *
 * This does NOT catch:
 * - Event handlers (use try-catch)
 * - Async code (use try-catch)
 * - Server-side rendering
 * - Errors in the boundary itself
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    this.setState({ error, errorInfo });

    // Call optional error handler (e.g., for Sentry, LogRocket, etc.)
    this.props.onError?.(error, errorInfo);

    // Error monitoring integration (Sentry)
    // To enable, install @sentry/react and configure in main.tsx:
    //
    // 1. Install: pnpm add @sentry/react
    //
    // 2. Initialize in main.tsx:
    //    import * as Sentry from '@sentry/react';
    //    Sentry.init({
    //      dsn: import.meta.env.VITE_SENTRY_DSN,
    //      environment: import.meta.env.MODE,
    //      tracesSampleRate: 0.1,
    //      replaysSessionSampleRate: 0.1,
    //      replaysOnErrorSampleRate: 1.0,
    //    });
    //
    // 3. Uncomment the following:
    // if (typeof window !== 'undefined' && window.Sentry) {
    //   Sentry.captureException(error, {
    //     extra: {
    //       componentStack: errorInfo.componentStack,
    //     },
    //     tags: {
    //       errorBoundary: 'true',
    //     },
    //   });
    // }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Allow custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = import.meta.env.DEV;
      const t = i18n.t.bind(i18n);

      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#f5f5f5',
        }}>
          <Result
            status="error"
            title={t('shared.errorBoundary.title')}
            subTitle={t('shared.errorBoundary.subtitle')}
            extra={
              <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap>
                  <Button type="primary" icon={<ReloadOutlined />} onClick={this.handleReset}>
                    {t('shared.errorBoundary.tryAgain')}
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={this.handleReload}>
                    {t('shared.errorBoundary.reloadPage')}
                  </Button>
                  <Button icon={<HomeOutlined />} onClick={this.handleGoHome}>
                    {t('shared.errorBoundary.goHome')}
                  </Button>
                </Space>

                {isDev && this.state.error && (
                  <div style={{
                    marginTop: 24,
                    padding: 16,
                    background: '#fff1f0',
                    borderRadius: 8,
                    textAlign: 'start',
                    maxWidth: 800,
                  }}>
                    <Space align="start">
                      <BugOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                      <div>
                        <Text strong style={{ color: '#ff4d4f' }}>
                          {t('shared.errorBoundary.devOnly')}
                        </Text>
                        <Paragraph
                          code
                          copyable
                          style={{
                            marginTop: 8,
                            marginBottom: 8,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {this.state.error.toString()}
                        </Paragraph>
                        {this.state.error.stack && (
                          <details>
                            <summary style={{ cursor: 'pointer', color: '#666' }}>
                              {t('shared.errorBoundary.stackTrace')}
                            </summary>
                            <pre style={{
                              fontSize: 11,
                              overflow: 'auto',
                              maxHeight: 200,
                              background: '#fafafa',
                              padding: 8,
                              borderRadius: 4,
                              marginTop: 8,
                            }}>
                              {this.state.error.stack}
                            </pre>
                          </details>
                        )}
                        {this.state.errorInfo?.componentStack && (
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', color: '#666' }}>
                              {t('shared.errorBoundary.componentStack')}
                            </summary>
                            <pre style={{
                              fontSize: 11,
                              overflow: 'auto',
                              maxHeight: 200,
                              background: '#fafafa',
                              padding: 8,
                              borderRadius: 4,
                              marginTop: 8,
                            }}>
                              {this.state.errorInfo.componentStack}
                            </pre>
                          </details>
                        )}
                      </div>
                    </Space>
                  </div>
                )}
              </Space>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
